<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <PrimeDialog
    :header="$t('config.dialog.addTitle')"
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    modal
    class="dialog-add-pair"
  >
    <div class="field">
      <label for="newKey">{{ $t("config.dialog.keyLabel") }}</label>
      <PrimeInputText
        id="newKey"
        v-model="newKey"
        class="w-full"
        :class="{ 'p-invalid': errors.key }"
      />
      <small v-if="errors.key" class="p-error">{{ errors.key }}</small>
    </div>
    <div class="field">
      <label for="newType">{{ $t("config.dialog.typeLabel") }}</label>
      <PrimeSelect
        id="newType"
        v-model="newType"
        :options="availableTypes"
        class="w-full"
      />
    </div>
    <div class="field">
      <label for="newValue">{{ $t("config.dialog.valueLabel") }}</label>
      <PrimeInputText
        id="newValue"
        v-model="newValue"
        class="w-full"
        :class="{ 'p-invalid': errors.value }"
      />
      <small v-if="errors.value" class="p-error">{{ errors.value }}</small>
    </div>
    <template #footer>
      <PrimeButton
        :label="$t('config.buttons.cancel')"
        icon="ri-close-line"
        class="p-button-text"
        @click="$emit('update:visible', false)"
      />
      <PrimeButton
        :label="$t('config.buttons.add')"
        icon="ri-check-line"
        @click="submit"
        autofocus
      />
    </template>
  </PrimeDialog>
</template>

<script>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import configService from "@/services/ConfigService.js";

export default {
  name: "AddPairInConfigDialog",
  components: {
    PrimeDialog: Dialog,
    PrimeButton: Button,
    PrimeInputText: InputText,
    PrimeSelect: Select,
  },
  props: {
    visible: { type: Boolean, default: false },
    existingKeys: { type: Array, default: () => [] },
  },
  emits: ["update:visible", "add-pair"],
  data() {
    return {
      newKey: "",
      newValue: "",
      newType: "string",
      availableTypes: ["string", "number", "boolean", "array", "object"],
      errors: { key: null, value: null },
    };
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.resetForm(); // Reset the form on each open
      }
    },
  },
  methods: {
    resetForm() {
      this.newKey = "";
      this.newValue = "";
      this.newType = "string";
      this.errors = { key: null, value: null };
    },
    submit() {
      this.errors = { key: null, value: null };
      let isValid = true;

      if (!this.newKey.trim()) {
        this.errors.key = this.$t("config.errors.keyRequired");
        isValid = false;
      } else if (this.existingKeys.includes(this.newKey.trim())) {
        this.errors.key = this.$t("config.errors.keyDuplicate");
        isValid = false;
      }

      const valueError = configService.validateValueByType(
        this.newValue,
        this.newType,
      );
      if (valueError) {
        this.errors.value = valueError;
        isValid = false;
      }

      if (!isValid) {
        return;
      }

      this.$emit("add-pair", {
        key: this.newKey,
        value: this.newValue,
        type: this.newType,
      });
      this.$emit("update:visible", false);
    },
  },
};
</script>

<!-- Non-scoped: Dialog teleported to body -->
<style>
.dialog-add-pair {
  width: 50vw;
  max-width: 500px;
}
</style>
